import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AuthContext, API } from '../../App';
import { MessageCircle, Phone, User, ChevronRight, Clock } from 'lucide-react';

export default function MobileChatList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSessions();
    const interval = setInterval(fetchSessions, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/sessions`);
      setSessions(response.data.filter(s => s.channel === 'chat').slice(0, 20));
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSessionTitle = (session) => {
    if (session.grade_code) {
      return t(`${session.grade_code.toLowerCase()}_name`);
    }
    return session.session_type?.replace('_', ' ') || 'Chat Session';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'pending': return 'bg-yellow-500';
      case 'completed': return 'bg-gray-400';
      default: return 'bg-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Header */}
      <div className="bg-primary text-white p-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-heading font-semibold">{t('chats')}</h1>
          <button
            onClick={() => navigate('/profile')}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <User size={24} />
          </button>
        </div>
      </div>

      {/* Chat List */}
      <div className="pb-20">
        {sessions.length === 0 ? (
          <div className="p-8 text-center text-[#718096]">
            <MessageCircle size={64} className="mx-auto mb-4 opacity-30" />
            <p className="text-lg mb-2">No chats yet</p>
            <p className="text-sm">Start a new conversation from Care Options</p>
          </div>
        ) : (
          sessions.map((session) => (
            <div
              key={session.id}
              data-testid={`chat-item-${session.id}`}
              onClick={() => navigate(`/mobile-chat/${session.id}`)}
              className="bg-white border-b border-gray-100 p-4 flex items-center gap-4 active:bg-gray-50 cursor-pointer transition-colors"
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                {session.channel === 'voice' ? (
                  <Phone size={24} className="text-primary" />
                ) : (
                  <MessageCircle size={24} className="text-primary" />
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-medium text-[#2D3748] truncate capitalize">
                    {getSessionTitle(session)}
                  </h3>
                  <span className="text-xs text-[#A0AEC0] ml-2">
                    {new Date(session.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${getStatusColor(session.status)}`}></span>
                  <span className="text-sm text-[#718096] capitalize">{session.status}</span>
                  {session.grade_code && (
                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full ml-auto">
                      {session.grade_code}
                    </span>
                  )}
                </div>
              </div>
              
              <ChevronRight size={20} className="text-[#A0AEC0] flex-shrink-0" />
            </div>
          ))
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-2 py-2 flex justify-around">
        <button
          onClick={() => navigate('/mobile-chats')}
          className="flex flex-col items-center py-2 px-4 text-primary"
        >
          <MessageCircle size={24} />
          <span className="text-xs mt-1">{t('chats')}</span>
        </button>
        <button
          onClick={() => navigate('/mobile-care-options')}
          className="flex flex-col items-center py-2 px-4 text-[#718096]"
        >
          <Clock size={24} />
          <span className="text-xs mt-1">{t('care_options')}</span>
        </button>
        <button
          onClick={() => navigate('/profile')}
          className="flex flex-col items-center py-2 px-4 text-[#718096]"
        >
          <User size={24} />
          <span className="text-xs mt-1">{t('profile')}</span>
        </button>
      </div>
    </div>
  );
}